import React from 'react';
import { ArrowRight } from 'lucide-react';
import BackgroundAnimation from '../../components/BackgroundAnimation';

const Branding = () => {
  const solutions = [
    { title: 'Logo Design', desc: 'Memorable logos that capture your brand essence', items: ['Multiple concepts', 'Vector formats', 'Brand guidelines', 'Unlimited revisions'] },
    { title: 'Brand Identity', desc: 'Complete visual identity system', items: ['Color palette', 'Typography system', 'Icon library', 'Style guide'] },
    { title: 'Brand Guidelines', desc: 'Comprehensive brand manual', items: ['Usage rules', 'Do\'s and don\'ts', 'Templates', 'Implementation guide'] },
    { title: 'Brand Strategy', desc: 'Strategic foundation for your brand', items: ['Brand positioning', 'Target audience', 'Brand voice', 'Competitive analysis'] }
  ];

  const process = [
    { num: '01', title: 'Discovery & Research', desc: 'Deep dive into your business, competitors, and target audience to understand your unique value' },
    { num: '02', title: 'Strategy & Positioning', desc: 'Develop brand strategy, positioning, and messaging that differentiates you in the market' },
    { num: '03', title: 'Design & Development', desc: 'Create visual identity elements including logo, colors, typography, and brand guidelines' },
    { num: '04', title: 'Implementation & Launch', desc: 'Roll out your new brand across all touchpoints with comprehensive implementation support' }
  ];

  const deliverables = [
    'Logo design (multiple concepts)',
    'Complete brand identity system',
    'Brand guidelines document',
    'Business card design',
    'Letterhead & stationery',
    'Social media templates',
    'Brand strategy document',
    'Implementation roadmap'
  ];

  const impact = [
    { metric: '95%', label: 'Brand Recognition Increase' },
    { metric: '78%', label: 'Customer Trust Improvement' },
    { metric: '2.3x', label: 'Brand Recall Rate' },
    { metric: '60%', label: 'Marketing Efficiency Gain' }
  ];

  return (
    <div className="min-h-screen page-transition" style={{ background: 'var(--bg-primary)', paddingTop: '80px', position: 'relative' }}>
      <BackgroundAnimation />
      
      <section className="py-24" style={{ padding: '120px 7.6923% 80px' }}>
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="display-huge mb-6" style={{ color: 'var(--text-primary)' }}>
            Branding &
            <span className="block" style={{ color: 'var(--brand-primary)' }}>Logo Redesign</span>
          </h1>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>Memorable Brand Identity Design</p>
          <p className="body-medium mb-8" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto 32px' }}>
            Create a powerful brand identity that resonates with your audience and stands out in the market. From logo design to complete brand strategy, we make you unforgettable.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://calendly.com/americanprimeforyou" target="_blank" rel="noopener noreferrer" className="button-text dark-button-animate inline-flex items-center space-x-2" style={{ background: 'var(--brand-primary)', color: '#000000', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}>
              <span>Start Your Brand</span>
              <ArrowRight size={20} />
            </a>
            <a href="/services" className="button-text dark-button-animate" style={{ background: 'rgba(255, 255, 255, 0.1)', color: 'var(--text-primary)', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}>View All Services</a>
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="display-large text-center mb-16" style={{ color: 'var(--text-primary)' }}>Complete Branding Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {solutions.map((solution, idx) => (
              <div key={idx} className="p-6 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--border-subtle)' }}>
                <h3 className="heading-2 mb-2" style={{ color: 'var(--brand-primary)' }}>{solution.title}</h3>
                <p className="body-small mb-4" style={{ color: 'var(--text-secondary)' }}>{solution.desc}</p>
                <ul className="space-y-1">
                  {solution.items.map((item, i) => (
                    <li key={i} className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      <span style={{ color: 'var(--brand-primary)' }}>• </span>{item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%', background: 'rgba(0, 255, 209, 0.02)', borderTop: '1px solid rgba(0, 255, 209, 0.1)', borderBottom: '1px solid rgba(0, 255, 209, 0.1)' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="display-large text-center mb-16" style={{ color: 'var(--text-primary)' }}>Our Branding Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, idx) => (
              <div key={idx}>
                <div className="display-large mb-4" style={{ color: 'var(--brand-primary)' }}>{step.num}</div>
                <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>{step.title}</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="display-large text-center mb-12" style={{ color: 'var(--text-primary)' }}>What You'll Receive</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {deliverables.map((item, idx) => (
              <div key={idx} className="flex items-center p-4 rounded" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--border-subtle)' }}>
                <span style={{ color: 'var(--brand-primary)', marginRight: '12px' }}>✓</span>
                <span className="body-medium" style={{ color: 'var(--text-primary)' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%', background: 'rgba(0, 255, 209, 0.02)', borderTop: '1px solid rgba(0, 255, 209, 0.1)', borderBottom: '1px solid rgba(0, 255, 209, 0.1)' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="display-large text-center mb-16" style={{ color: 'var(--text-primary)' }}>Brand Impact</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {impact.map((stat, idx) => (
              <div key={idx} className="text-center p-6 rounded-lg" style={{ background: 'rgba(0, 255, 209, 0.05)', border: '1px solid var(--brand-primary)' }}>
                <div className="display-large mb-2" style={{ color: 'var(--brand-primary)' }}>{stat.metric}</div>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="display-large mb-6" style={{ color: 'var(--text-primary)' }}>Ready to Build Your Brand?</h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>Let's create a brand that stands out and drives results</p>
          <a href="https://calendly.com/americanprimeforyou" target="_blank" rel="noopener noreferrer" className="button-text dark-button-animate inline-flex items-center space-x-2" style={{ background: 'var(--brand-primary)', color: '#000000', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}>
            <span>Start Branding</span>
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Branding;